import React from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  renderSecondaryBlock,
  renderTertiaryBlock,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import {
  BackgroundColors as ThemeBackgroundColors,
  Variants,
} from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { ContentType, layoutOptions } from "./types/prop-types";

/** Content Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.Small,
  tertiaryBlockSize: TertiaryBlockSize.Default,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.dark,
};

let applyDefaultColor = false;

export const Content = primaryBlock<ContentType>(
  blockConfig,
  ({
    SecondaryBlock,
    TertiaryBlock,
    items,
    tagline,
    header,
    text,
    image,
    layoutProps,
  }: ContentType) => {
    if (image.url) {
      applyDefaultColor = false;
      layoutProps.backgroundColor = ThemeBackgroundColors.none;
    } else if (!applyDefaultColor) {
      applyDefaultColor = true;
      layoutProps.backgroundColor = ThemeBackgroundColors.neutral;
    }

    const headerIsLeft = layoutProps.headerPlacement === "left";
    const alignmentIsLeft = layoutProps.textAlignment === "left";

    return (
      <Box className="primary-content-two-col-grid-media-v1">
        <Box className="section_layout285">
          <Box className="padding-global">
            <Box className="container-large padding-section-large">
              <Box className="w-embed" />
              <Box
                sx={{
                  gridTemplateColumns: headerIsLeft ? "0.5fr 1fr" : "1fr 0.5fr",
                }}
                className="layout285_component"
              >
                <Box
                  sx={{ order: headerIsLeft ? -1 : 1 }}
                  className="layout285_content"
                >
                  {tagline && (
                    <Box className="margin-bottom margin-xsmall">
                      <Variants.TagLine>{tagline}</Variants.TagLine>
                    </Box>
                  )}
                  <Box className="margin-bottom margin-small">
                    <Variants.Heading>{header}</Variants.Heading>
                  </Box>
                  <Variants.ParagraphMedium>{text}</Variants.ParagraphMedium>
                  {renderSecondaryBlock(SecondaryBlock)}
                  {renderTertiaryBlock(TertiaryBlock)}
                </Box>
                <Box className="layout285_list">
                  {items.map(
                    (
                      item: {
                        icon: string;
                        title: string;
                        description: string;
                      },
                      i: number
                    ) => (
                      <Box key={i} className="layout285_item">
                        <Box
                          sx={{
                            justifyContent: alignmentIsLeft ? "left" : "center",
                          }}
                          className="margin-bottom margin-xsmall icon-placement"
                        >
                          <Box>
                            <Variants.IconSmall
                              icon={item.icon}
                              sx={{
                                marginLeft: layoutProps.showIconBackground
                                  ? "0"
                                  : "-20px",
                              }}
                              hasBg={layoutProps.showIconBackground}
                              className={
                                !layoutProps.showIconBackground
                                  ? "icon-no-bg"
                                  : ""
                              }
                            />
                          </Box>
                        </Box>
                        <Box>
                          <Box className="margin-bottom margin-small">
                            <Variants.Heading as="h3" variant="h4">
                              {item.title}
                            </Variants.Heading>
                          </Box>
                          <Variants.ParagraphDefault>
                            {item.description}
                          </Variants.ParagraphDefault>
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Variants.OpacityWrapper className="layout285_background-image-wrapper">
            <Variants.OpacityLayer className="image-overlay-layer" />
            <Variants.ImageNoRadius
              src={image.url}
              loading="lazy"
              alt={image.alt}
              className="layout285_background-image"
            />
          </Variants.OpacityWrapper>
        </Box>
      </Box>
    );
  }
);

Content.defaultProps = getDefaultProps<ContentType>(defaults);

export const Element: KameleonElement = {
  component: Content,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
